import React, { useEffect, useState } from "react"
import { AiOutlineLock } from "react-icons/ai" // Added AiOutlineBell for notification icon
import CustomGanttNurse from "../components/NurseAssignments/CustomGanttNurse"
import NurseListContainer from "../components/NurseAssignments/NurseListView/NurseListContainer"
import LunchBreakPopup from "../components/NurseView/LunchBreakPopup"
import NotificationSidebar from "../components/NurseView/NotificationSidebar"
import ConfirmationDialog from "../components/DailyCalendarFiles/CustomCalendar/ConfirmationDialog"
import { useSelector, useDispatch } from "react-redux"
import { FaArrowRightArrowLeft } from "react-icons/fa6"
import { updateSchedule } from "../features/schedule/scheduleSlice"
import NotificationPopup from "../components/DailyCalendarFiles/CustomCalendar/NotificationPopup" // Import NotificationPopup
import CalendarSettings from "../components/DailyCalendarFiles/CustomCalendar/CalendarSettings"
import { useAuth0 } from "@auth0/auth0-react"
import { useQueryClient } from "@tanstack/react-query"
import useFetchSettings from "../hooks/useFetchSettings"
import useFetchSchedules from "../hooks/useFetchSchedules/useFetchSchedules"
import useFetchNotifications from "../hooks/useFetchNotifications"
import { FaPlay } from "react-icons/fa"
import {
	setIsEditing,
	setOptimizeData,
	resetPatientEdits,
	updatePatientField,
	updatePatientInSchedule
} from "../features/schedule/scheduleSlice"
import store from "../store"
import {
	currentlyNurseLunchBreakOngoing,
	currentlyNurseLunchBreakFinished,
	currentlyNurseLunchBreakFuture,
	calculateTotalAcuity
} from "../utils/realTimeNurseUtils"
import {
	convertMinutesTo12HHMM,
	getDateFormatted,
	getFirstOpenDateToday,
	getCurrentTime,
	getClinicTimes
} from "../utils/generalUtils"
import {
	getStartTime,
	getEndTime,
	currentlyRealTimeOngoing,
	rangesOverlap,
	confirmedCancellationNoShow
} from "../utils/realTimeUtils"
import { getThemeColors } from "../utils/themeColors"
import { getScheduleFromMongo } from "../utils/mongoUtils"
import { useSchedulePatch } from "../hooks/useSchedulePatch"
import { PageLoader } from "../components/Authentication/page-loader"
import useLogging from "../hooks/useLogging"
import { setSandboxMode } from "../features/schedule/sandboxSlice"
import {
	getNursePatientIds,
	getNursePatients
} from "../features/schedule/derivedAssignments"
import { setTomorrowMode } from "../features/schedule/tomorrowSlice"
import ExtraRoleInfo from "../components/DailyCalendarFiles/CustomCalendar/ExtraRoleInfo"

const getReassignedPatients = (patientSchedule, currentNurseId) => {
	return patientSchedule.filter(patient => patient.transferredBy === currentNurseId)
}

// TODO: we need to use the new reducers to move patients around without breaking the invariants

const NurseView = ({ sidebarOpen }) => {
	const { user, getAccessTokenSilently } = useAuth0()
	const { colorScheme } = useSelector(state => state.user)
	const [modalOpen, setModalOpen] = useState(false) // To open and close the modal
	const [reassignedPatients, setReassignedPatients] = useState([])
	const [reassignedAcuity, setReassignedAcuity] = useState(0)
	const dispatch = useDispatch()
	const queryClient = useQueryClient()
	const [notification, setNotification] = useState({ visible: false, message: "", success: true })
	const logging = useLogging()
	const schedulePatch = useSchedulePatch({ isTomorrowMode: false, sandbox: false })
	useFetchSchedules("NurseView")
	const { notificationsData } = useFetchNotifications(true) // TODO: here is the difference vs custom calendar... lets see if this gets faster without this
	const { settingsData } = useFetchSettings()

	const patientSchedule = useSelector(state => state.schedule.patientSchedule)
	const nurseSchedule = useSelector(state => state.schedule.nurseSchedule)
	const chairSchedule = useSelector(state => state.schedule.chairSchedule)

	const [chartKey, setChartKey] = useState(0)

	const testEnvironment = user.org_id === "org_lkg3WMhi3Q2l92iT"
	const centerOpen = settingsData?.centerOpen
	const centerClosedDates = settingsData?.center_closed_dates || []
	const todayDate = getFirstOpenDateToday(centerOpen, testEnvironment, centerClosedDates)
	const today = getDateFormatted(todayDate)

	const { openTime, closeTime } = getClinicTimes(settingsData, todayDate)

	const currentTime = getCurrentTime(false, false)

	const [isSidebarOpen, setIsSidebarOpen] = useState(false) // To toggle the sidebar

	const toggleSidebar = () => {
		setIsSidebarOpen(!isSidebarOpen)
	}

	const isLocked = useSelector(state => state.schedule.isLocked)

	const currentNurseEmail = user.email

	// Find the nurse with the currentNurseId
	const currentNurse = nurseSchedule.find(
		nurse => nurse.nurseEmail?.toLowerCase() === currentNurseEmail?.toLowerCase()
	)
	const currentNurseId = currentNurse?.nurseId

	// Filter notifications where additionalData.requestedBy.sub matches user.sub
	const filteredNotifications =
		notificationsData &&
		notificationsData.filter(
			notification =>
				notification.additionalData?.requestedBy?.sub?.toLowerCase() ===
					currentNurseId?.toLowerCase() && !notification.decision
		)

	// Count of notifications that match the filter
	const notificationCount = filteredNotifications ? filteredNotifications.length : 0

	const editedPatients = useSelector(state => state.schedule.editedPatients)

	// TODO: theres a lot of useeffects calculating random things - but they seem relatively light weight lets console log within all of them to see the time

	useEffect(() => {
		if (!currentNurse) {
			return
		}

		const currentNursePatientIds = currentNurse
			? getNursePatientIds(patientSchedule, currentNurse.nurseId)
			: []

		if (notificationCount === 0 && currentNurse) {
			currentNursePatientIds.forEach(patientId => {
				const editedPatient = editedPatients.find(
					patient => patient.patientId === patientId
				)
				if (editedPatient && Object.values(editedPatient).some(value => value === true)) {
					dispatch(resetPatientEdits({ patientId }))
				}
			})
		} else {
			// For each notification, process its proposed changes
			filteredNotifications &&
				filteredNotifications.forEach(notification => {
					const proposedChanges = notification.additionalData.proposedChanges

					const { patientId } = proposedChanges

					if (currentNursePatientIds.includes(patientId)) {
						const editedPatient = editedPatients.find(
							patient => patient.patientId === patientId
						)

						if (editedPatient && !editedPatient.pendingChanges) {
							dispatch(updatePatientField({ patientId, fieldName: "pendingChanges" }))
							const patient = patientSchedule.find(p => p.patientId === patientId)
							const updatedPatient = { ...patient, pendingChanges: true }
							dispatch(updatePatientInSchedule({ updatedPatient }))
						}
					}
				})
		}
		// `dispatch` from useDispatch is stable across renders per react-redux docs.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentNurse?.nurseId, patientSchedule, dispatch, notificationCount])

	const chargeNurse = useSelector(state => state.schedule.chargeNurse)

	const [requestedNurse, setRequestedNurse] = useState(null)

	const breakDuration = settingsData?.break_duration

	const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false)
	const [minutesElapsed, setMinutesElapsed] = useState(0)

	const [isLunchBreakPopupOpen, setIsLunchBreakPopupOpen] = useState(false)
	const [isRequestPatientsPopupOpen, setIsRequestPatientsPopupOpen] = useState(false)

	useEffect(() => {
		if (requestedNurse === null && currentNurse != null) {
			const getTransferredPatients = () => {
				return (
					patientSchedule &&
					patientSchedule.filter(
						patient =>
							patient.assignedNurse === currentNurseId &&
							patient.transferredBy !== null &&
							patient.transferredBy !== undefined
					)
				)
			}

			const transferredPatientList = getTransferredPatients()

			if (transferredPatientList.length > 0) {
				// Assuming all transferred patients are from the same nurse, you can get the first one's transferredBy value
				const transferringNurseId = transferredPatientList[0].transferredBy
				const transferringNurse = nurseSchedule.find(
					nurse => nurse.nurseId === transferringNurseId
				)

				if (transferringNurse) {
					setRequestedNurse(transferringNurse.nurseName) // Store the nurse who reassigned the patients
				}
			}
		}
		// React setState setters are stable — `currentNurse` is read but not required as a dep here.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [requestedNurse, patientSchedule, currentNurseId, nurseSchedule])

	const [isPopupOpen, setIsPopupOpen] = useState(false)

	useEffect(() => {
		if (currentlyNurseLunchBreakOngoing(currentNurse)) {
			const intervalId = setInterval(() => {
				const timeElapsed = currentTime - currentNurse.actualLunchBreakStart

				if (timeElapsed > 1.25 * breakDuration) {
					setIsPopupOpen(true)
					clearInterval(intervalId) // Stop checking once the condition is met
				}
			}, 60000) // Check every minute

			return () => clearInterval(intervalId) // Cleanup on unmount or dependency change
		}
		// `currentTime` is read on tick; including it would re-run the interval continuously.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentNurse, breakDuration])

	const handleConfirm = () => {
		setIsPopupOpen(false)
		endLunchBreakProcess()
	}

	const handleCancel = () => {
		setIsPopupOpen(false)
		dispatch(setIsEditing(false))
	}

	const handleStartLunchBreak = async () => {
		// Exclude the current nurse from the available nurses list

		if (currentTime > currentNurse.scheduledEndTime) {
			setNotification({
				visible: true,
				message: "You cannot start your lunch break as your shift has ended!",
				success: false
			})
			return
		}

		// Calculate the end time for the reassignment window
		const reassignmentEndTime = currentTime + breakDuration

		// Filter patients who are currently receiving treatment or will start within the break duration
		const patientsToReassign = patientSchedule.filter(patient => {
			// Check if the patient is currently receiving treatment or will start within the next break duration minutes
			return (
				patient.assignedNurse === currentNurseId &&
				!confirmedCancellationNoShow(patient) &&
				rangesOverlap(
					[getStartTime(patient, false, false), getEndTime(patient, false, false)],
					[currentTime, reassignmentEndTime]
				)
			)
		})

		let maxAcuity = 0

		// Iterate through every minute from current time to reassignment end time
		for (let time = currentTime; time <= reassignmentEndTime; time++) {
			let currentTotalAcuity = 0

			patientsToReassign.forEach(patient => {
				if (currentlyRealTimeOngoing(patient)) {
					currentTotalAcuity += patient.acuity
				}
			})

			// Update max acuity if the current total is greater
			if (currentTotalAcuity > maxAcuity) {
				maxAcuity = currentTotalAcuity
			}
		}

		setReassignedAcuity(maxAcuity)
		setReassignedPatients(patientsToReassign.map(patient => patient.patientId))
		setIsLunchBreakPopupOpen(true)
	}

	const handleConfirmLunchBreak = async selectedNurseId => {
		const token = await getAccessTokenSilently({
			audience: process.env.REACT_APP_API_IDENTIFIER
		})

		const currentState = store.getState()["schedule"]

		const mergedData = await getScheduleFromMongo(
			token,
			today,
			dispatch,
			currentState,
			false,
			false,
			null,
			setOptimizeData,
			logging
		)

		// Update the patient schedule with the new nurse assignment for the reassigned patients
		const updatedPatientSchedule = mergedData.patientSchedule.map(patient => {
			if (reassignedPatients.includes(patient.patientId)) {
				return { ...patient, assignedNurse: selectedNurseId, transferredBy: currentNurseId }
			}
			return patient
		})

		const updatedNurseSchedule = mergedData.nurseSchedule.map(nurse => {
			if (nurse.nurseId === currentNurseId) {
				return {
					...nurse,
					transferredPatients: reassignedPatients,
					actualLunchBreakStart: currentTime,
					actualLunchBreakEnd: null
				}
			}
			return nurse
		})

		// Dispatch updates to the store
		dispatch(
			updateSchedule({
				patientSchedule: updatedPatientSchedule,
				nurseSchedule: updatedNurseSchedule
			})
		)

		await schedulePatch.mutateAsync({ date: today, setNotification })
	}

	const handleRequestPatients = async selectedNurseId => {
		if (currentTime > currentNurse.scheduledEndTime) {
			setNotification({
				visible: true,
				message: "You cannot request patients as your shift has ended!",
				success: false
			})
			return
		}

		const token = await getAccessTokenSilently({
			audience: process.env.REACT_APP_API_IDENTIFIER
		})

		const currentState = store.getState()["schedule"]

		const mergedData = await getScheduleFromMongo(
			token,
			today,
			dispatch,
			currentState,
			false,
			false,
			null,
			setOptimizeData,
			logging
		)
		const transferringNurse = mergedData.nurseSchedule.find(
			nurse => nurse.nurseId === selectedNurseId
		)
		setRequestedNurse(transferringNurse.nurseName)

		if (currentlyNurseLunchBreakFinished(transferringNurse)) {
			setNotification({
				visible: true,
				message: "The selected nurse has already finished their lunch break.",
				success: false
			})

			return
		}

		if (currentlyNurseLunchBreakOngoing(transferringNurse)) {
			setNotification({
				visible: true,
				message: "The selected nurse is currently on a lunch break.",
				success: false
			})
			return
		}

		// Calculate the end time for the reassignment window
		const reassignmentEndTime = currentTime + breakDuration

		// Filter patients who are currently receiving treatment or will start within the break duration
		const patientsToReassign = mergedData.patientSchedule.filter(patient => {
			// Check if the patient is currently receiving treatment or will start within the next break duration minutes
			return (
				patient.assignedNurse === selectedNurseId &&
				!confirmedCancellationNoShow(patient) &&
				rangesOverlap(
					[getStartTime(patient, false, false), getEndTime(patient, false, false)],
					[currentTime, reassignmentEndTime]
				)
			)
		})

		// Update the patient schedule so the current nurse manages the selected nurse's patients
		const updatedPatientSchedule = mergedData.patientSchedule.map(patient => {
			if (patientsToReassign.includes(patient)) {
				return { ...patient, assignedNurse: currentNurseId, transferredBy: selectedNurseId }
			}
			return patient
		})

		const updatedNurseSchedule = mergedData.nurseSchedule.map(nurse => {
			if (nurse.nurseId === selectedNurseId) {
				return {
					...nurse,
					transferredPatients: patientsToReassign.map(p => p.patientId),
					actualLunchBreakStart: currentTime,
					actualLunchBreakEnd: null
				}
			}
			return nurse
		})

		// Dispatch updates to the store
		dispatch(
			updateSchedule({
				patientSchedule: updatedPatientSchedule,
				nurseSchedule: updatedNurseSchedule
			})
		)
		await schedulePatch.mutateAsync({ date: today, setNotification })
	}

	const handleEndLunchBreak = async () => {
		dispatch(setIsEditing(true))
		const elapsedMinutes = currentTime - currentNurse?.actualLunchBreakStart

		if (elapsedMinutes < breakDuration) {
			setMinutesElapsed(elapsedMinutes)
			setIsConfirmDialogOpen(true)
			return
		}

		endLunchBreakProcess()
	}

	const endLunchBreakProcess = async () => {
		const token = await getAccessTokenSilently({
			audience: process.env.REACT_APP_API_IDENTIFIER
		})

		const currentState = store.getState()["schedule"]

		const mergedData = await getScheduleFromMongo(
			token,
			today,
			dispatch,
			currentState,
			false,
			false,
			null,
			setOptimizeData,
			logging
		)

		const currentReassignedPatients = getReassignedPatients(
			mergedData.patientSchedule,
			currentNurseId
		).map(patient => patient.patientId)
		setReassignedPatients(currentReassignedPatients)

		// Reassign patients back to the original nurse and remove transferredBy field
		const updatedPatientSchedule = mergedData.patientSchedule.map(patient => {
			if (currentReassignedPatients.includes(patient.patientId)) {
				return {
					...patient,
					assignedNurse: currentNurseId,
					transferredBy: null // Reset the transferredBy field
				}
			}
			return patient
		})

		const updatedNurseSchedule = mergedData.nurseSchedule.map(nurse => {
			if (nurse.nurseId === currentNurseId) {
				return {
					...nurse,
					transferredPatients: [],
					actualLunchBreakEnd: currentTime
				}
			}
			return nurse
		})

		// Dispatch updates to the store
		dispatch(
			updateSchedule({
				patientSchedule: updatedPatientSchedule,
				nurseSchedule: updatedNurseSchedule
			})
		)
		await schedulePatch.mutateAsync({ date: today, setNotification })

		// Reset state
		setReassignedPatients([])
		setReassignedAcuity(0)
	}

	const handleEndRequestPatients = async () => {
		const token = await getAccessTokenSilently({
			audience: process.env.REACT_APP_API_IDENTIFIER
		})

		const currentState = store.getState()["schedule"]

		const mergedData = await getScheduleFromMongo(
			token,
			today,
			dispatch,
			currentState,
			false,
			false,
			null,
			setOptimizeData,
			logging
		)

		// Get the transferring nurse's ID
		const transferringNurseId = requestedNurse
			? mergedData.nurseSchedule.find(nurse => nurse.nurseName === requestedNurse)?.nurseId
			: null

		// Update the patient schedule to reassign patients back to the transferring nurse
		const updatedPatientSchedule = mergedData.patientSchedule.map(patient => {
			if (patient.transferredBy === transferringNurseId) {
				return {
					...patient,
					assignedNurse: transferringNurseId,
					transferredBy: null // Reset the transferredBy field
				}
			}
			return patient
		})

		const updatedNurseSchedule = mergedData.nurseSchedule.map(nurse => {
			if (nurse.nurseId === transferringNurseId) {
				return {
					...nurse,
					transferredPatients: [],
					actualLunchBreakEnd: currentTime
				}
			}
			return nurse
		})

		// Dispatch updates to the store
		dispatch(
			updateSchedule({
				patientSchedule: updatedPatientSchedule,
				nurseSchedule: updatedNurseSchedule
			})
		)
		await schedulePatch.mutateAsync({ date: today, setNotification })

		// Reset state
		setReassignedPatients([])
		setReassignedAcuity(0)
		setRequestedNurse(null)
	}

	useEffect(() => {
		// Set sandbox and tomorrow mode to false when entering NurseView
		dispatch(setSandboxMode(false))
		dispatch(setTomorrowMode(false))
	}, [dispatch])

	if (!notificationsData || !settingsData || !patientSchedule || !nurseSchedule) {
		return <PageLoader />
	}

	const getFirstName = fullName => {
		if (!fullName) return ""
		return fullName.split(" ")[0]
	}

	if (!isLocked) {
		return (
			<div
				className={`flex flex-col items-center justify-center h-screen text-center ${sidebarOpen ? "pl-64" : "pl-0"}`}
			>
				<AiOutlineLock className="text-6xl text-black-900 mb-4" />
				<p className="text-4xl font-bold text-black-900 mb-4">Nurse View</p>
				<p className="text-lg font-semibold text-black-900">
					Today's charge nurse has not submitted the schedule yet. Check back later!
				</p>
			</div>
		)
	}

	if (!currentNurse) {
		return (
			<div
				className={`flex flex-col items-center justify-center h-screen text-center ${
					sidebarOpen ? "pl-64" : "pl-0"
				}`}
			>
				<AiOutlineLock className="text-6xl text-black-900 mb-4" />
				<p className="text-4xl font-bold text-black-900 mb-4">Nurse View</p>
				<p className="text-lg font-semibold text-black-900">
					Please ask {chargeNurse ? chargeNurse.nurseName : "the charge nurse"} to add you
					to today's schedule to view your assignments.
				</p>
			</div>
		)
	}

	const colorsBackground = getThemeColors(colorScheme)

	const fetchNotifications = async () => {
		try {
			await queryClient.invalidateQueries({ queryKey: ["notifications"] })
		} catch (error) {
			logging.warn("Error refreshing notifications:", error)
		}
	}

	const firstName = user.name.split(" ")[0]

	const hasScheduledLunchBreak =
		currentNurse?.scheduledLunchBreakStart !== null &&
		currentNurse?.scheduledLunchBreakEnd !== null &&
		currentNurse.scheduledLunchBreakStart >= currentNurse.scheduledStartTime &&
		currentNurse.scheduledLunchBreakEnd <= currentNurse.scheduledEndTime

	return (
		<div className={`w-screen ${sidebarOpen ? "pl-64" : "pl-0"}`}>
			<div className="overflow-x-hidden">
				<CalendarSettings modalOpen={modalOpen} setModalOpen={setModalOpen} />

				{/* Render NotificationPopup here */}
				{notification.visible && (
					<NotificationPopup
						message={notification.message}
						success={notification.success}
						onUndo={() =>
							setNotification({ visible: false, message: "", success: true })
						}
					/>
				)}

				<NotificationSidebar
					isSidebarOpen={isSidebarOpen}
					toggleSidebar={toggleSidebar}
					notificationCount={notificationCount}
					filteredNotifications={filteredNotifications}
					fetchNotifications={fetchNotifications}
				/>

				<div className="my-5 flex h-auto sm:h-16 flex-col sm:flex-row items-start sm:items-center justify-between bg-white px-4 sm:px-6 lg:px-8">
					<h1 className="font-bold text-4xl font-manrope text-custom-blue mb-4 sm:mb-0">
						Hi {firstName},
					</h1>
				</div>

				<main>
					<div className="px-4 sm:px-6 lg:px-8">
						<div className="nurse-view">
							<div className="daily-overview">
								<h2 className="font-bold text-2xl font-manrope text-custom-blue my-4">
									Daily Overview
								</h2>
								<p>
									Today, you will see{" "}
									{(currentNurse
										? getNursePatients(patientSchedule, currentNurse.nurseId).length
										: 0) + (reassignedPatients?.length || 0)}{" "}
									patients, starting from{" "}
									{currentNurse?.scheduledStartTime
										? convertMinutesTo12HHMM(currentNurse?.scheduledStartTime)
										: openTime}{" "}
									to{" "}
									{currentNurse?.scheduledEndTime
										? convertMinutesTo12HHMM(currentNurse?.scheduledEndTime)
										: closeTime}
									. Your total patient acuity over the day is{" "}
									{calculateTotalAcuity(currentNurse, patientSchedule)}
									{hasScheduledLunchBreak
										? `, and your ${breakDuration}-minute lunch break is suggested at ${convertMinutesTo12HHMM(currentNurse?.scheduledLunchBreakStart || "")} - ${convertMinutesTo12HHMM(currentNurse?.scheduledLunchBreakEnd || "")}.`
										: "."}
								</p>
							</div>

							<div className="my-4 flex space-x-4">
								{/* Check if patients have been reassigned to the current nurse */}
								{!requestedNurse ? (
									// No nurse has reassigned their patients, show the appropriate lunch break action or message
									currentlyNurseLunchBreakFuture(currentNurse) ? (
										// Show "Start Lunch Break" button if both start and end are null/undefined
										<>
											<button
												className="bg-customBlue text-white px-4 py-2 rounded-md hover:bg-custom-blue flex items-center"
												onClick={handleStartLunchBreak}
											>
												<FaPlay className="mr-2" />
												Start Lunch Break
											</button>

											{isLunchBreakPopupOpen && (
												<LunchBreakPopup
													isOpen={isLunchBreakPopupOpen}
													setIsOpen={setIsLunchBreakPopupOpen}
													onConfirm={handleConfirmLunchBreak}
													onCancel={handleCancel}
													acuity={reassignedAcuity}
													currentNurseId={currentNurseId}
													popupTitle="Start Lunch Break"
													popupMessage="Reassign your patients to another nurse:"
													dropdownLabel="Enter Nurse Assigned..."
												/>
											)}
										</>
									) : currentlyNurseLunchBreakOngoing(currentNurse) ? (
										<button
											onClick={handleEndLunchBreak}
											className="bg-customBlue text-white px-4 py-2 rounded-md hover:bg-custom-blue"
										>
											End Lunch Break
										</button>
									) : (
										<p className="py-2">
											Thanks for taking your lunch break today! Your lunch
											break was from{" "}
											{convertMinutesTo12HHMM(
												currentNurse?.actualLunchBreakStart
											)}{" "}
											-{" "}
											{convertMinutesTo12HHMM(
												currentNurse?.actualLunchBreakEnd
											)}
											. <br />
										</p>
									)
								) : currentlyNurseLunchBreakFuture(currentNurse) ? (
									// Show the warning message if a nurse has reassigned their patients
									<>
										<div>
											<p className="align-middle">
												Thanks for monitoring {getFirstName(requestedNurse)}
												's patients 😄, please ask them to end their lunch
												break before you can start your lunch break.
											</p>
											<button
												onClick={handleEndRequestPatients}
												className="bg-customBlue text-white px-4 py-2 rounded-md hover:bg-custom-blue align-middle my-4"
											>
												Return Patients
											</button>
										</div>
									</>
								) : (
									<>
										<div>
											<p className="align-middle">
												Thanks for monitoring {getFirstName(requestedNurse)}
												's patients 😄, please ask them to end their lunch
												break before you take anymore patients.
											</p>
											<button
												onClick={handleEndRequestPatients}
												className="bg-customBlue text-white px-4 py-2 rounded-md hover:bg-custom-blue align-middle my-4"
											>
												Return {getFirstName(requestedNurse)}'s Patients
											</button>
										</div>
									</>
								)}

								{/* Only show the Request Patients button if the current nurse hasn't already requested patients */}
								{!currentlyNurseLunchBreakOngoing(currentNurse) &&
									!requestedNurse && (
										<>
											<button
												className="bg-customBlue text-white px-4 py-2 rounded-md hover:bg-custom-blue flex items-center"
												onClick={() => setIsRequestPatientsPopupOpen(true)}
											>
												<FaArrowRightArrowLeft className="mr-2" />
												Request {requestedNurse}'s Patients
											</button>

											{isRequestPatientsPopupOpen && (
												<LunchBreakPopup
													isOpen={isRequestPatientsPopupOpen}
													setIsOpen={setIsRequestPatientsPopupOpen}
													onConfirm={handleRequestPatients}
													onCancel={handleCancel}
													acuity={0}
													currentNurseId={currentNurseId}
													popupTitle="Request Patients" // Custom title
													popupMessage="Select the nurse whose patients you want to reassign:" // Custom message
													dropdownLabel="Select Nurse..." // Custom dropdown label
												/>
											)}
										</>
									)}
							</div>

							<ConfirmationDialog
								title="End Lunch Break Early?"
								message={`Are you sure you want to end lunch? It's only been ${minutesElapsed} minutes.`}
								isOpen={isConfirmDialogOpen}
								onConfirm={() => {
									endLunchBreakProcess()
									setIsConfirmDialogOpen(false)
								}}
								onCancel={() => setIsConfirmDialogOpen(false)}
							/>

							<ConfirmationDialog
								title="Return from Lunch?"
								message="It seems your lunch break has exceeded the expected duration. Have you returned from lunch?"
								isOpen={isPopupOpen}
								onConfirm={handleConfirm}
								onCancel={handleCancel}
							/>

							<div className="patient-schedule">
								{nurseSchedule &&
								nurseSchedule.length > 0 &&
								currentNurseId !== null &&
								currentNurseId !== undefined ? (
									<CustomGanttNurse
										nurseId={currentNurseId}
										handleChartRerender={() => setChartKey(chartKey + 1)}
										setNotification={setNotification}
										patientSchedule={patientSchedule}
										nurseSchedule={nurseSchedule}
										chairSchedule={chairSchedule}
										openTime={openTime}
										closeTime={closeTime}
										sandbox={false}
										isTomorrowMode={false}
									/>
								) : (
									<div>Loading nurse data...</div>
								)}
							</div>

							<div className="flex justify-end items-center my-5">
								<div className="flex flex-wrap items-center">
									{colorsBackground.map((color, index) => (
										<div
											key={index}
											className="flex items-center m-2 ms-3 me-3 font-bold text-sm"
										>
											<div
												className={`mr-2 w-7 h-7 ${color} rounded-full`}
											></div>
											{index}
										</div>
									))}
								</div>
							</div>

							<div className="patient-list">
								<h2 className="font-bold text-2xl font-manrope text-custom-blue my-4">
									Patient List
								</h2>
								{nurseSchedule &&
								nurseSchedule.length > 0 &&
								currentNurseId !== null &&
								currentNurseId !== undefined ? (
									<NurseListContainer
										nurseId={currentNurseId}
										setNotification={setNotification}
										isToday={true}
									/>
								) : (
									<div>Loading nurse data...</div>
								)}
							</div>

							<ExtraRoleInfo setNotification={setNotification} />
						</div>
					</div>
				</main>
			</div>
		</div>
	)
}

export default NurseView
